import { useEffect, useState } from "react";
import {
  Loader2,
  Clock,
  LogIn,
  CalendarClock,
  Phone,
  Mail,
  MessageCircle,
  AlertTriangle,
} from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { es } from "date-fns/locale";
import apiClient from "@/lib/api-client";

// Contrato de GET /api/admin/organizations/:id/health (backend ya desplegado,
// no tocar). Ojo: la clave top-level de la respuesta es "health", no "data".
interface HealthPeriodCounts {
  total: number;
  bySource: Record<string, number>;
  byStatus: Record<string, number>;
}

interface OrganizationHealth {
  organizationId: string;
  generatedAt: string;
  reservations: {
    last7d: HealthPeriodCounts;
    last30d: HealthPeriodCounts;
    lastReservationAt: string | null;
    upcoming: number;
  };
  voice: {
    enabled: boolean;
    last30d: {
      totalCalls: number;
      reservationsCreated: number;
      successRatePct: number | null;
      problematic: number;
    };
  };
  email: {
    last30d: {
      sent: number;
      opened: number;
      clicked: number;
      failed: number;
    };
    coverage: string;
  };
  whatsapp: {
    last30d: {
      sent: number;
      failed: number;
      skipped: number;
    };
    coverage: string;
  };
  lastLoginAt: string | null;
}

interface HealthResponse {
  success: boolean;
  health: OrganizationHealth;
}

const SOURCE_LABELS: Record<string, string> = {
  widget: "Widget",
  dashboard: "Dashboard",
  phone: "Teléfono",
  "walk-in": "Sin reserva",
  web: "Web",
  google: "Google",
  mobile_app: "App móvil",
  "phone-ai": "Carmen (voz)",
};

const STATUS_LABELS: Record<string, string> = {
  pending: "Pendiente",
  confirmed: "Confirmada",
  double_confirmed: "Doble confirmada",
  arrived: "Llegó",
  seated: "Sentada",
  completed: "Completada",
  cancelled: "Cancelada",
  no_show: "No-show",
};

function labelFor(map: Record<string, string>, rawKey: string): string {
  if (map[rawKey]) return map[rawKey];
  const normalized = rawKey.replace(/[-_]/g, " ");
  return normalized.charAt(0).toUpperCase() + normalized.slice(1);
}

function formatRelative(dateString: string | null): string {
  if (!dateString) return "Sin datos";
  try {
    return formatDistanceToNow(new Date(dateString), {
      addSuffix: true,
      locale: es,
    });
  } catch {
    return "Sin datos";
  }
}

function extractErrorMessage(err: unknown, fallback: string): string {
  if (err && typeof err === "object" && "response" in err) {
    const response = (
      err as { response?: { data?: { error?: string; message?: string } } }
    ).response;
    return response?.data?.error || response?.data?.message || fallback;
  }
  return fallback;
}

function MetricBox({
  label,
  value,
  highlight = false,
}: {
  label: string;
  value: number | string;
  highlight?: boolean;
}) {
  return (
    <div
      className={`rounded-lg border px-3 py-2 ${
        highlight ? "bg-red-50 border-red-200" : "bg-gray-50 border-gray-200"
      }`}
    >
      <p className="text-[11px] uppercase tracking-wide text-gray-400">
        {label}
      </p>
      <p
        className={`text-lg font-bold ${highlight ? "text-red-700" : "text-gray-900"}`}
      >
        {value}
      </p>
    </div>
  );
}

function BreakdownList({
  counts,
  labels,
}: {
  counts: Record<string, number>;
  labels: Record<string, string>;
}) {
  const entries = Object.entries(counts)
    .filter(([, value]) => value > 0)
    .sort((a, b) => b[1] - a[1]);

  if (entries.length === 0) {
    return <p className="text-xs text-gray-400 italic">Sin datos</p>;
  }

  const max = Math.max(...entries.map(([, value]) => value));

  return (
    <ul className="space-y-1.5">
      {entries.map(([key, value]) => (
        <li key={key} className="flex items-center gap-2 text-xs">
          <span
            className="w-24 flex-shrink-0 truncate text-gray-600"
            title={labelFor(labels, key)}
          >
            {labelFor(labels, key)}
          </span>
          <div className="h-2 flex-1 overflow-hidden rounded-full bg-gray-100">
            <div
              className="h-2 rounded-full bg-primary-500"
              style={{ width: `${(value / max) * 100}%` }}
            />
          </div>
          <span className="w-6 flex-shrink-0 text-right font-medium text-gray-900">
            {value}
          </span>
        </li>
      ))}
    </ul>
  );
}

function ReservationsPeriodCard({
  title,
  data,
}: {
  title: string;
  data: HealthPeriodCounts;
}) {
  return (
    <div className="rounded-lg border border-gray-200 p-4">
      <div className="mb-3 flex items-center justify-between">
        <h4 className="text-sm font-semibold text-gray-900">{title}</h4>
        <span className="text-lg font-bold text-gray-900">{data.total}</span>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <p className="mb-1.5 text-[11px] uppercase tracking-wide text-gray-400">
            Por canal
          </p>
          <BreakdownList counts={data.bySource} labels={SOURCE_LABELS} />
        </div>
        <div>
          <p className="mb-1.5 text-[11px] uppercase tracking-wide text-gray-400">
            Por estado
          </p>
          <BreakdownList counts={data.byStatus} labels={STATUS_LABELS} />
        </div>
      </div>
    </div>
  );
}

interface OrganizationHealthPanelProps {
  organizationId: string;
}

export default function OrganizationHealthPanel({
  organizationId,
}: OrganizationHealthPanelProps) {
  const [health, setHealth] = useState<OrganizationHealth | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    setError("");
    setHealth(null);

    apiClient
      .get<HealthResponse>(`/admin/organizations/${organizationId}/health`)
      .then((res) => {
        if (cancelled) return;
        if (res.data?.health) {
          setHealth(res.data.health);
        } else {
          setError("Respuesta inesperada del servidor.");
        }
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(
          extractErrorMessage(
            err,
            "No se pudo cargar la salud de la organización.",
          ),
        );
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [organizationId]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center gap-2 py-10 text-sm text-gray-500">
        <Loader2 className="h-5 w-5 animate-spin" />
        Cargando salud de la organización…
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
        {error}
      </div>
    );
  }

  if (!health) return null;

  return (
    <div className="space-y-5">
      {/* Cabecera: última actividad */}
      <div className="flex flex-wrap items-center gap-x-6 gap-y-1 rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-600">
        <span className="flex items-center gap-1.5">
          <Clock className="h-4 w-4 flex-shrink-0 text-gray-400" />
          Última reserva:{" "}
          <strong className="text-gray-900">
            {formatRelative(health.reservations.lastReservationAt)}
          </strong>
        </span>
        <span className="flex items-center gap-1.5">
          <LogIn className="h-4 w-4 flex-shrink-0 text-gray-400" />
          Último login:{" "}
          <strong className="text-gray-900">
            {formatRelative(health.lastLoginAt)}
          </strong>
        </span>
      </div>

      {/* Reservas */}
      <div>
        <div className="mb-3 flex items-center gap-2">
          <CalendarClock className="h-4 w-4 text-gray-500" />
          <h3 className="text-sm font-semibold text-gray-900">Reservas</h3>
          <span className="ml-auto text-xs text-gray-500">
            Próximas:{" "}
            <strong className="text-gray-900">
              {health.reservations.upcoming}
            </strong>
          </span>
        </div>
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          <ReservationsPeriodCard
            title="Últimos 7 días"
            data={health.reservations.last7d}
          />
          <ReservationsPeriodCard
            title="Últimos 30 días"
            data={health.reservations.last30d}
          />
        </div>
      </div>

      {/* Voz (Carmen) */}
      <div className="rounded-lg border border-gray-200 p-4">
        <div className="mb-3 flex items-center gap-2">
          <Phone className="h-4 w-4 text-gray-500" />
          <h3 className="text-sm font-semibold text-gray-900">
            Centralita IA (Carmen) · 30 días
          </h3>
        </div>
        {!health.voice.enabled ? (
          <span className="inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600">
            No activada
          </span>
        ) : (
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <MetricBox
              label="Llamadas"
              value={health.voice.last30d.totalCalls}
            />
            <MetricBox
              label="Reservas creadas"
              value={health.voice.last30d.reservationsCreated}
            />
            <MetricBox
              label="Tasa de éxito"
              value={
                health.voice.last30d.successRatePct !== null
                  ? `${health.voice.last30d.successRatePct}%`
                  : "—"
              }
            />
            <MetricBox
              label="Problemáticas"
              value={health.voice.last30d.problematic}
              highlight={health.voice.last30d.problematic > 0}
            />
          </div>
        )}
      </div>

      {/* Email */}
      <div className="rounded-lg border border-gray-200 p-4">
        <div className="mb-3 flex items-center gap-2">
          <Mail className="h-4 w-4 text-gray-500" />
          <h3 className="text-sm font-semibold text-gray-900">
            Email · 30 días
          </h3>
        </div>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          <MetricBox label="Enviados" value={health.email.last30d.sent} />
          <MetricBox label="Abiertos" value={health.email.last30d.opened} />
          <MetricBox label="Clics" value={health.email.last30d.clicked} />
          <MetricBox
            label="Fallidos"
            value={health.email.last30d.failed}
            highlight={health.email.last30d.failed > 0}
          />
        </div>
      </div>

      {/* WhatsApp */}
      <div className="rounded-lg border border-gray-200 p-4">
        <div className="mb-3 flex items-center gap-2">
          <MessageCircle className="h-4 w-4 text-gray-500" />
          <h3 className="text-sm font-semibold text-gray-900">
            WhatsApp · 30 días
          </h3>
        </div>
        <div className="mb-3 grid grid-cols-3 gap-3">
          <MetricBox label="Enviados" value={health.whatsapp.last30d.sent} />
          <MetricBox
            label="Fallidos"
            value={health.whatsapp.last30d.failed}
            highlight={health.whatsapp.last30d.failed > 0}
          />
          <MetricBox
            label="Omitidos"
            value={health.whatsapp.last30d.skipped}
          />
        </div>
        {health.whatsapp.coverage !== "full" && (
          <p className="flex items-start gap-1.5 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
            <AlertTriangle className="h-3.5 w-3.5 flex-shrink-0 mt-0.5" />
            Cobertura parcial: por ahora solo algunos flujos registran
            WhatsApp, el total real puede ser mayor.
          </p>
        )}
      </div>

      <p className="text-right text-[11px] text-gray-400">
        Datos generados {formatRelative(health.generatedAt)}
      </p>
    </div>
  );
}
